/**
 * @typedef RuleEvaluatorOptions
 * @property {boolean} [debug]
 *
 * @type RuleEvaluatorOptions
 */
const defaultOpts = {
  debug: false,
};

export default class RuleEvaluator {
  /**
   * @param {RuleEvaluatorOptions} opts
   */
  constructor(opts = {}) {
    this.answers = new Map();
    this.debug = opts.debug || defaultOpts.debug;
    this._splitOperations = {
      OR: ' or ',
      AND: ' and ',
    };
  }

  setAnswers(values) {
    this.answers.clear();
    for (let answer of values) {
      this.answers.set(answer.appkey, answer.value);
    }
  }

  isTrue(expression) {
    const logicalExpressionStr = Array.isArray(expression)
      ? expression.join(' and ')
      : expression;
    let expr = logicalExpressionStr
      .replace(/ or /gi, ' || ')
      .replace(/ and /gi, ' && ');
    this._debug('START | Evaluating Expression:', {
      expression,
      logicalExpression: expr,
    });

    const expressionSplitByAnd = this._convertExpressionToArray(
      expression,
      this._splitOperations.AND
    );
    for (let currentExpression of expressionSplitByAnd) {
      const expressSplitByOr = this._convertExpressionToArray(
        currentExpression,
        this._splitOperations.OR
      );
      for (let x of expressSplitByOr) {
        //hasAnswered
        let found = x.match(/hasAnswered\(([^)]*)\)/g);
        if (found) {
          expr = this._parseHasAnswered(found, expr);
        }

        //hasAnsweredWith
        found = x.match(/hasAnsweredWith\(([^)]*)\)/g);
        if (found) {
          expr = this._parseHasAnsweredWith(found, expr);
        }

        //comparison operations
        found = x.match(/[^(](.*)[<>!=]=?(.*)[^)]/g);
        if (found) {
          expr = this._parseComparisonOperation(found, expr);
        }
      }
    }

    this._debug('END | Final expression', expr);
    try {
      return new Function(`return (${expr})`)();
    } catch (ex) {
      if (this.debug) console.log('RuleEvaluator.isTrue catch(ex)', ex.message);
      return false;
    }
  }

  _parseHasAnswered(matchedExpressions, expr) {
    for (let func of matchedExpressions) {
      this._debug('Parsing | HasAnswered', func);
      let p = func.slice(13, -2);
      let x = this._hasAnswered(p);
      expr = expr.replace(func, x);
    }
    return expr;
  }

  _parseHasAnsweredWith(matchedExpressions, expr) {
    for (let func of matchedExpressions) {
      this._debug('Parsing | HasAnsweredWith', func);
      let p = func.slice(16, -1);
      let p1 = p.slice(0, p.indexOf(',')).trim().slice(1, -1);
      let p2 = p.slice(1 + p.indexOf(',')).trim();
      let x = this._hasAnsweredWith(p1, p2);
      expr = expr.replace(func, x);
    }
    return expr;
  }

  _parseComparisonOperation(matchedExpressions, expr) {
    const isBoolean = (str) =>
      str.toLowerCase() === 'true' ||
      str.toLowerCase() === 'false' ||
      typeof str === 'boolean';

    for (let func of matchedExpressions) {
      this._debug('Parsing | Comparison Operation', func);
      let operationsFound = func.match(/(\s*)[<>!=]=?(\s*)/g);
      let op = operationsFound.length
        ? operationsFound[0].replace(/\s+/g, '')
        : '==';
      let p = func.replace(/\s+/g, '').split(op);
      let left = isBoolean(p[0])
        ? p[0]
        : this._convertOperationWithVariables(p[0]);
      let right = isBoolean(p[1])
        ? p[1]
        : this._convertOperationWithVariables(p[1]);
      let x = `${left} ${op} ${right}`;
      expr = expr.replace(func, x);
    }
    return expr;
  }

  _convertOperationWithVariables(operation) {
    this._debug('Converting an operation with variables: ', operation);
    let result = operation;
    operation = operation.replace(/\s+/g, '');
    operation = operation.replace(/(\s*)[+\-*%/]=?(\s*)/g, '~~~');
    operation = operation.replace(/[(.*)]/g, '~~~');
    let variables = operation.split('~~~');
    if (variables) {
      for (let v of variables) {
        let value = v;
        // noinspection JSCheckFunctionSignatures
        if (isNaN(value)) {
          value = this.answers.has(v) ? this.answers.get(v) : '0';
        }
        result = result.replace(v, value);
      }
    }
    this._debug('Converted an operation with variables to:', result);
    return result;
  }

  _hasAnsweredWith(name, values) {
    if (!this.answers.has(name)) {
      return false;
    }

    let foundAnswer = this.answers.get(name);
    let foundValue = foundAnswer.find((v) => values.indexOf(v) !== -1);
    return !!foundValue;
  }

  _hasAnswered(name) {
    return this.answers.has(name);
  }

  _convertExpressionToArray(expressions, splitBy) {
    const splitExpression = (exprStr) => exprStr.trim().split(splitBy);

    if (Array.isArray(expressions)) {
      let expressionArray = [];
      for (let x of expressions) {
        expressionArray = [...expressionArray, ...splitExpression(x)];
      }
      return expressionArray;
    } else {
      return splitExpression(expressions);
    }
  }

  _debug(msg = '', params = '') {
    if (this.debug) {
      console.log(`DEBUG | RuleEvaluator | ${msg}`, params);
    }
  }

  /**
   * Static factory method for class.
   *
   * @param {RuleEvaluatorOptions} opts
   * @returns {RuleEvaluator}
   */
  static initialize(opts = {}) {
    return new this(opts);
  }
}
